import { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AWSCredentials } from '../types/auth';
import { authService } from '../services/authService';
import { featureFlags } from '../config/featureFlags';
import { PortalErrorKind, toPortalError } from '../services/customerError';

/**
 * Authentication state interface
 * Manages user authentication status and AWS credentials
 */
export interface AuthState {
  isAuthenticated: boolean;
  email: string | null;
  awsCredentials: AWSCredentials | null;
  loading: boolean;
  error: PortalErrorKind | null;
  hasChannelAccess: boolean;
}

/**
 * Authentication context interface
 * Provides authentication state and methods to child components
 */
export interface AuthContextValue {
  authState: AuthState;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */
export const AuthenticationContext = createContext<AuthContextValue | null>(null);

/**
 * Authentication Provider Props
 */
interface AuthenticationProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides context to child components
 *
 * Requirements:
 * - 7.4: React Context for authentication state management
 * - 2.1: SessionToken stored in httpOnly cookie (managed by backend)
 * - 2.5: Session persistence across page refreshes
 */
export function AuthenticationProvider({ children }: AuthenticationProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // When auth is bypassed (local dev), start as authenticated immediately
    if (featureFlags.bypassAuth) {
      return {
        isAuthenticated: true,
        email: 'local-dev@example.com',
        awsCredentials: null,
        loading: false,
        error: null,
        hasChannelAccess: true,
      };
    }
    return {
      isAuthenticated: false,
      email: null,
      awsCredentials: null,
      loading: true,
      error: null,
      hasChannelAccess: true,
    };
  });

  /**
   * Refresh the user session by validating SessionToken cookie
   * Backend validates SessionToken and returns AWS credentials
   *
   * Requirements:
   * - 2.1: SessionToken sent automatically via httpOnly cookie
   * - 2.2: Backend validates SessionToken with ISAC
   * - 2.3: Backend returns AWS credentials (not IdToken)
   */
  const refreshSession = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Use authService to validate session
      const data = await authService.getSession();

      setAuthState({
        isAuthenticated: true,
        email: data.userEmail,
        awsCredentials: {
          accessKeyId: data.accessKeyId,
          secretAccessKey: data.secretAccessKey,
          sessionToken: data.sessionToken,
        },
        loading: false,
        error: null,
        hasChannelAccess: data.hasChannelAccess ?? true,
      });
    } catch (error) {
      const portalError = toPortalError(error);
      const isSessionExpired = portalError.kind === 'sessionExpired';

      setAuthState({
        isAuthenticated: false,
        email: null,
        awsCredentials: null,
        loading: false,
        error: isSessionExpired ? null : portalError.kind,
        hasChannelAccess: true,
      });
    }
  };

  /**
   * Initiate login flow by redirecting to Builder ID
   *
   * Requirements:
   * - 1.1: Backend initiates ISAC StartFederation
   * - 1.2: Redirect to Builder ID login page
   */
  const login = async () => {
    logoutRequestedRef.current = false;
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      // Generate random state for CSRF protection
      const state = crypto.randomUUID();

      // Use authService to start login
      const data = await authService.startLogin(state);

      // Store state in sessionStorage for validation after callback
      sessionStorage.setItem('auth_state', state);

      // Redirect to Builder ID login
      const urlParams = new URLSearchParams(data.parameters);
      window.location.href = `${data.redirectUrl}?${urlParams.toString()}`;
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: toPortalError(error, 'authenticationFailed').kind,
      }));
    }
  };

  /**
   * Logout user by revoking session
   *
   * Requirements:
   * - Revoke ISAC session token via backend
   * - Clear local authentication state
   */
  const logout = async () => {
    logoutRequestedRef.current = true;
    try {
      setAuthState((prev) => ({ ...prev, loading: true, error: null }));

      await authService.logout();

      setAuthState({
        isAuthenticated: false,
        email: null,
        awsCredentials: null,
        loading: false,
        error: null,
        hasChannelAccess: true,
      });
    } catch (error) {
      // Clear local state even if backend revocation fails (network error or
      // server error). Note: on network errors the cookie may still be present,
      // so the next page load could silently re-authenticate via session refresh.
      console.error('Failed to revoke session on backend:', error);
      setAuthState({
        isAuthenticated: false,
        email: null,
        awsCredentials: null,
        loading: false,
        error: 'signOutIncomplete',
        hasChannelAccess: true,
      });
    }
  };

  // Stable ref to refreshSession so the interval callback never goes stale
  const refreshSessionRef = useRef(refreshSession);
  refreshSessionRef.current = refreshSession;

  // Stable ref to logout for the same reason
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // Guards against the session refresh re-authenticating the user after a
  // failed logout (where the cookie may still be valid server-side).
  const logoutRequestedRef = useRef(false);

  /**
   * On component mount, attempt to refresh session using httpOnly cookie.
   * The browser sends the SessionToken cookie automatically with credentials: 'include'.
   * If no valid session exists, getSession returns 401 and we stay unauthenticated.
   *
   * Requirements:
   * - 2.5: Session persistence across page refreshes
   */
  useEffect(() => {
    // Skip session refresh on /callback — AuthCallback handles its own
    // finish-federation → refreshSession flow. Calling refreshSession here
    // before the cookie is set causes a race condition (401 from getSession).
    // Also skip if logout was requested — the cookie may still be valid but
    // we must not silently re-authenticate the user after they signed out.
    if (
      window.location.pathname !== '/callback' &&
      !logoutRequestedRef.current &&
      !featureFlags.bypassAuth
    ) {
      refreshSession();
    }
  }, []);

  /**
   * Periodic session validation with ISAC.
   * ISAC security requirement: validate long-term session every 5-15 minutes
   * to detect revoked sessions even when the user is idle.
   * See: https://w.amazon.com/bin/view/AWSIdentity/Onboarding/IdentityCenter/Security/#HSessionHandling
   *
   * Also enforces idle timeout: if no user activity for 15 minutes, logs out
   * instead of refreshing. This addresses the pentest finding for excessive
   * session duration while maintaining the ISAC validation requirement.
   */
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const resetActivity = () => {
      lastActivityRef.current = Date.now();
    };
    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);
    window.addEventListener('scroll', resetActivity);
    return () => {
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
      window.removeEventListener('scroll', resetActivity);
    };
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    const SESSION_VALIDATION_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

    const intervalId = setInterval(() => {
      if (logoutRequestedRef.current) return;

      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime > IDLE_TIMEOUT_MS) {
        console.info('Session idle timeout reached, logging out');
        logoutRef.current();
      } else {
        refreshSessionRef.current();
      }
    }, SESSION_VALIDATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [authState.isAuthenticated]);

  const contextValue: AuthContextValue = {
    authState,
    login,
    logout,
    refreshSession,
  };

  return (
    <AuthenticationContext.Provider value={contextValue}>{children}</AuthenticationContext.Provider>
  );
}
